import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const BahrainPage = () => {
  const bahrainData = {
    name: 'Bahrain',
    region: 'GCC',
    color: '#C09060',
    metaTitle: 'Pharma & Nutraceutical Registration in Bahrain | NHRA | CTD/eCTD Dossiers',
    metaDescription: 'Understand how to register pharmaceutical and nutraceutical products in Bahrain. NHRA requirements, CTD/eCTD dossier format, food supplement rules, required documents and market opportunity in a high-income, insurance-driven GCC market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Bahrain',
      subtitle: 'A high-income GCC market with strong NCD burden, insurance-driven demand and CTD/eCTD-based registration under NHRA — highly relevant for premium generics and well-positioned supplements.',
      cta: 'Start Your Bahrain Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~1.57M' },
      { label: 'Income Level', value: 'High-income economy' },
      { label: 'Health Expenditure (% of GDP)', value: '~4.3%' },
      { label: 'Pharma Imports (2023)', value: '~USD 428M' },
      { label: 'Market Structure', value: 'Insurance-driven' },
      { label: 'Regulatory Authority', value: 'NHRA' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Insurance-Driven, Import-Heavy, Branded',
      stats: [
        'NHRA oversees licensing via Pharmacy & Pharmaceutical Products Regulation (PPR)',
        'Pharmaceutical imports ~USD 427.8 million in 2023',
        'Patented medicines and foreign drug companies currently dominate',
        'Government aims to grow generics for cost-control and insurance reforms',
        'Full data on quality, safety, efficacy required before product sale',
        'PPR section approves all pharmaceutical applications',
        'Health Products covered by dedicated NHRA guideline',
        'Food supplements supervised as food products with classification criteria'
      ],
      opportunities: [
        'Small population but high income and per-capita health spend',
        'Heavily import-driven pharma market',
        'Ideal for high-quality generics and differentiated nutraceuticals',
        'Strong insurance penetration supports premium positioning',
        'NHRA uses CTD/eCTD aligning with global practice'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: NCD-Dominant and Lifestyle-Driven',
      subtitle: 'Four main NCDs (CVD, cancer, diabetes, chronic respiratory disease) account for ~75% of deaths, with lifestyle and dietary factors driving high disease burden.',
      categories: [
        {
          name: 'Non-Communicable Diseases — Dominant Burden',
          items: [
            'NCDs account for ~75% of deaths in Bahrain',
            'Nearly 1 in 5 adults dying from NCD before age 70',
            'Raised blood pressure: ~16% of adults',
            'Raised blood glucose: ~9% of adults',
            'Adult obesity: ~29% overall (25% men, 36% women)',
            'Pharma: Antihypertensives, antidiabetics, lipid-lowering, cardiovascular, renal & metabolic therapies, oncology, chronic respiratory',
            'Nutra: Cardiometabolic support, weight-management, antioxidant supplements for NCD prevention'
          ]
        },
        {
          name: 'Nutrition & Lifestyle Factors',
          items: [
            'Obesity, diabetes, hypertension, hyperlipidaemia linked to rapid socio-economic change',
            'Public-health dietary guidelines promoting healthier patterns',
            'Implementation still evolving - room for evidence-based supplements',
            'Pharma: Chronic disease portfolios, essential generics, hospital lines for secondary/tertiary care',
            'Nutra: Vitamins/minerals, cardiovascular & metabolic support, joint/ageing products, sports nutrition'
          ]
        },
        {
          name: 'Best-Fit Product Categories',
          items: [
            'Pharma: Chronic therapies (antidiabetics, antihypertensives, lipid-lowering), specialist products (oncology, biologic biosimilars, respiratory & allergy)',
            'Nutra: Heart & metabolic health supplements, weight-management, lifestyle support, vitamins/minerals, sports-nutrition as special foods'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'National Health Regulatory Authority (NHRA)',
      description: 'NHRA\'s Pharmacy & Pharmaceutical Products Regulation (PPR) handles medicines and health products. Medicine Licensing Guideline (v3.0 2020, v4.x onwards) sets procedures. CTD/eCTD format required with baseline sequence 0000. Health Products Licensing Guideline for non-medicine products. Product Classification Guideline clarifies supplement vs medicine.',
      pharma: {
        timeline: 'Several months from complete submission; complex products or queries may extend timeline',
        requirements: [
          'Bahrain-licensed company acts as MAH and applicant',
          'CTD/eCTD dossier required - baseline sequence 0000',
          'Complete package covering quality, safety, efficacy per international CTD standards',
          'Drug registration application, medication file (dossier), price certificate required',
          'Alcohol-free certificate and pig-products-free certificate mandatory (GCC requirement)',
          'Quality analysis file and samples required',
          'PPR members review CTD dossiers and assess benefit-risk',
          'Query process for clarifications or additional data',
          'Medicine licence granted with pricing formalization',
          'Renewal via Medicine Renewal Guidelines using CTD/eCTD sequences'
        ],
        insight: 'NHRA requires CTD/eCTD format increasingly. Religious-sensitivity documents (alcohol/pig-free certificates) mandatory. Assessment thorough with benefit-risk balance. Lifecycle managed via eCTD sequences.'
      },
      nutra: {
        timeline: 'Varies based on classification pathway and documentation completeness',
        requirements: [
          'Classification via NHRA Product Classification Guideline first',
          'Food supplements: Vitamin/mineral within upper intake levels, multivitamin/mineral supplements, functional foods',
          'Special foods route: Public Health Directorate licensing (nutritional supplements, energy drinks)',
          'Health products route: NHRA PPR for non-medicine health products (cosmetics, wellness, borderline)',
          'Pharma-like dosage forms + therapeutic claims → full CTD path as medicine',
          'Food-like forms + lower nutrients + wellness claims → food supplement/special food route',
          'Application form per chosen route required',
          'Composition details, COAs, GMP evidence required',
          'Label with compliant ingredients, usage, warnings, no unauthorized therapeutic claims'
        ],
        insight: 'Clear pre-classification critical to avoid delays. Products with pharma-like forms and therapeutic claims risk medicine classification requiring full CTD. Food supplements managed by Public Health Directorate as special foods.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with NHRA (High-Level Pathway)',
      steps: [
        'Local Representative / MAH - Bahrain-licensed company acts as MAH and applicant',
        'Dossier Format CTD/eCTD - Baseline sequence 0000, lifecycle via eCTD sequences',
        'Complete CTD package - Quality, safety, efficacy per international standards',
        'Application & Forms - Drug registration application, medication file (dossier)',
        'Price certificate submission',
        'Alcohol-free certificate (mandatory GCC requirement)',
        'Pig-products-free certificate (mandatory GCC requirement)',
        'Quality analysis file and product samples',
        'Scientific Assessment - PPR members review CTD and balance benefit-risk',
        'Query process for clarifications/additional data',
        'Approval & Pricing - Medicine licence granted, pricing formalized per NHRA rules',
        'Renewal & Lifecycle - Medicine Renewal Guidelines govern variations using CTD/eCTD'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Food Supplement Pathways in Bahrain',
      steps: [
        'Classification determination via Product Classification Guideline',
        'Route selection: Special foods (Public Health Directorate) OR Health products (NHRA PPR)',
        'Food supplements criteria: Vitamin/mineral within limits, multivitamin/mineral, functional foods',
        'Special foods licensing: Nutritional supplements, energy drinks, athletes\' food, baby food',
        'Health products licensing: Non-medicine health products, cosmetics, wellness, borderline',
        'Application form per chosen route',
        'Composition details (quantitative formula) with COAs',
        'Manufacturer GMP/quality system evidence',
        'Finished-product test reports (microbiological, contaminants)',
        'Label/artwork - ingredients list, usage, warnings, no unauthorized claims',
        'Proof of regulatory status in country of origin',
        'Avoid pharma dosage forms + therapeutic claims to stay in food/health product route'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (NHRA - CTD/eCTD)': [
        'NHRA medicine licence application form',
        'Full CTD dossier (Modules 1-5): Administrative, Quality/CMC, stability, specs, non-clinical, clinical',
        'Price certificate',
        'Alcohol-free certificate (mandatory GCC requirement)',
        'Pig-products-free certificate (mandatory GCC requirement)',
        'Quality analysis file',
        'Product samples',
        'MAH and manufacturer licences',
        'Legal documents'
      ],
      'Food Supplements / Health Products': [
        'Application form (special food OR NHRA health-product licence per route)',
        'Composition details (quantitative formula)',
        'COAs (certificates of analysis)',
        'Manufacturer GMP/quality system evidence',
        'Finished-product test reports (microbiological, contaminants)',
        'Label and artwork with compliant ingredients list',
        'Usage instructions and warnings',
        'No unauthorized therapeutic claims',
        'Proof of regulatory status in country of origin'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Bahrain Belongs in Your GCC Launch Plan',
      reasons: [
        'High-income population with strong insurance penetration and private-sector care',
        'Large pharma import bill (~USD 428M) despite small population',
        'Dominated by patented medicines - space for high-quality generics under cost-containment',
        'NCDs cause ~75% of deaths (obesity, diabetes, hypertension prevalent)',
        'Cardiometabolic therapies and supplements structurally important',
        'NHRA uses CTD/eCTD, classification and reliance-style approaches',
        'Aligns with global regulatory practice',
        'Good gateway reference in GCC for regional expansion',
        'Strong per-capita health spend ideal for premium products'
      ],
      bestPortfolio: [
        'Pharma: Chronic therapies (antidiabetics, antihypertensives, lipid-lowering), specialist products (oncology, biologic biosimilars, respiratory & allergy)',
        'Nutra: Heart & metabolic health, weight-management, lifestyle support, vitamins/minerals, sports-nutrition special foods'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Bahrain',
      services: [
        'Portfolio Prioritisation for GCC - Identify SKUs suited to Bahrain NCD profile and pricing',
        'CTD/eCTD Dossier Development - Build/adapt to NHRA Medicine Licensing Guideline',
        'Religious-Sensitivity Documentation - Alcohol/pig-free attestations and pricing structuring',
        'Supplement vs Medicine Classification - Work through Product Classification Guidelines',
        'Label & Claims Compliance - Arabic/English labels with GCC-appropriate health claims',
        'Lifecycle & GCC Cluster Strategy - Integrate into wider GCC plan using reusable CTD modules',
        'Local MAH Coordination - Support Bahrain-licensed company setup and documentation'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in Bahrain?',
        answer: 'The National Health Regulatory Authority (NHRA), via the Pharmacy & Pharmaceutical Products Regulation (PPR) section, regulates the licensing and marketing of medicines.'
      },
      {
        question: 'What dossier format does NHRA require?',
        answer: 'NHRA expects submissions in CTD format, increasingly in eCTD, with baseline sequence 0000 and lifecycle submissions for renewals/variations.'
      },
      {
        question: 'Are there specific religious-sensitivity documents required?',
        answer: 'Yes. The public "Drug Registration" service specifies alcohol-free and pig-products-free certificates as mandatory attachments alongside the CTD dossier and price certificate.'
      },
      {
        question: 'How are food supplements regulated?',
        answer: 'Food supplements are supervised as food products, with NHRA classifying them via its Product Classification Guideline and the Public Health Directorate licensing "special foods" (including nutritional supplements and energy drinks).'
      },
      {
        question: 'How long does registration take?',
        answer: 'Official guidelines focus on requirements rather than fixed timeframes. In practice, approvals usually take several months from complete submission; complex products or extensive NHRA queries can extend this.'
      }
    ]
  };

  return <CountryTemplate countryData={bahrainData} />;
};
